/* pdp2 — product page family, V2 design (Sept 2026)
 *
 * Hero gallery viewer, chapter navigation, the backdrop placement rule, scroll
 * reveals, the animated disclosure rows, the before/after comparison card, the
 * ingredient explorer, the how-to-use morning/evening toggle and the lab
 * study-to-reference cross-highlight.
 * All motion is skipped under prefers-reduced-motion.
 */
(function () {
  'use strict';

  // Every section in this family emits its own <script src="pdp2.js">, and two
  // tags with the same src execute twice. Bail on the second run so the
  // document-level listeners below are only ever registered once.
  if (window.__pdp2Loaded) return;
  window.__pdp2Loaded = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var DESKTOP = '(min-width: 900px)';

  function on(el, type, fn, opts) {
    if (el) el.addEventListener(type, fn, opts);
  }

  /* ----------------------------------------------------------------------
   * Hero backdrop placement
   *
   * The photo is cover-fitted, so at most widths the browser's crop puts the
   * product wherever it lands. The design asks for the product to sit above
   * the gallery strip. We compute the object-position that puts the subject
   * (at --focus-x/--focus-y of the photo) over the strip's centre, at natural
   * cover scale with no zoom. Under 900px the rule stands down and the
   * declared object-position applies.
   * -------------------------------------------------------------------- */
  /* measureFold and placeBackdrop live in snippets/pdp2-hero-place.liquid, an
   * inline script at the end of the hero, so the first placement happens while
   * the page is still parsing instead of after every deferred script. These
   * delegate to it for resizes and late layout changes. */
  function measureFold(hero) {
    if (window.pdp2HeroPlace) window.pdp2HeroPlace.measureFold(hero);
  }

  function placeBackdrop(hero) {
    if (window.pdp2HeroPlace) window.pdp2HeroPlace.placeBackdrop(hero);
  }

  /* ----------------------------------------------------------------------
   * Drag to scroll the thumbnail strip.
   *
   * Touch scrolls it natively, but with the scrollbar hidden a mouse or
   * trackpad has no way to move a horizontal overflow container at all. This
   * adds click-and-drag for pointer devices, and swallows the click that
   * follows a drag so dragging never opens the viewer.
   * -------------------------------------------------------------------- */
  function initStripDrag(strip) {
    if (!strip || !window.PointerEvent) return;
    var down = false;
    var startX = 0;
    var startScroll = 0;
    var moved = 0;

    on(strip, 'pointerdown', function (e) {
      if (e.pointerType === 'touch') return; // native scrolling handles this
      down = true;
      moved = 0;
      startX = e.clientX;
      startScroll = strip.scrollLeft;
    });

    on(strip, 'pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > moved) moved = Math.abs(dx);
      if (moved > 4) {
        strip.classList.add('is-dragging');
        strip.scrollLeft = startScroll - dx;
        e.preventDefault();
      }
    });

    function end() {
      down = false;
      strip.classList.remove('is-dragging');
    }
    on(strip, 'pointerup', end);
    on(strip, 'pointercancel', end);
    on(strip, 'pointerleave', end);

    // Capture phase, so it beats the thumbnail's own click handler.
    on(
      strip,
      'click',
      function (e) {
        if (moved > 4) {
          e.preventDefault();
          e.stopPropagation();
          moved = 0;
        }
      },
      true
    );
  }

  /* ----------------------------------------------------------------------
   * Add to cart
   *
   * The theme's own product module would do this, but it is coupled to
   * main-product's DOM and throws against this markup. So the form is posted to
   * the cart API here and the theme's drawer is opened by clicking the header
   * cart link, which the theme already wires to it. Any failure falls back to a
   * native submit, so the button always does something.
   * -------------------------------------------------------------------- */
  // The cart drawer on this store is Rebuy Smart Cart, not the theme's
  // quick-cart. Rebuy binds the header cart icon itself and reads the cart
  // live, so clicking that icon opens a current drawer. The theme's own cart
  // link is the fallback, and a plain navigation the last resort.
  function openCartDrawer() {
    var trigger = document.querySelector('[data-js-cart-icon], [data-cart-link], a[href$="/cart"]');
    if (trigger) trigger.click();
    else window.location.href = '/cart';
  }

  function initAddToCart(root, selector) {
    var form = root.querySelector(selector || 'form[data-product-form]');
    if (!form || !window.fetch) return;

    on(form, 'submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('[data-add-to-cart]');
      if (btn) btn.setAttribute('aria-busy', 'true');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add-to-cart failed');
          return res.json();
        })
        .then(function () {
          if (btn) btn.removeAttribute('aria-busy');
          openCartDrawer();
        })
        .catch(function () {
          if (btn) btn.removeAttribute('aria-busy');
          form.submit(); // native POST; does not re-enter this handler
        });
    });
  }

  /* ----------------------------------------------------------------------
   * Hero gallery viewer
   * -------------------------------------------------------------------- */
  function initHero(hero) {
    // Both sections in this family load pdp2.js, and two <script> tags with the
    // same src do execute twice. Without this the gallery gets two independent
    // controllers, and the second one's focus bookkeeping fights the first's.
    if (hero.dataset.pdp2Init) return;
    hero.dataset.pdp2Init = '1';

    var thumbs = Array.prototype.slice.call(hero.querySelectorAll('[data-pdp2-thumb]'));
    var viewer = hero.querySelector('[data-pdp2-viewer]');
    var zoom = hero.querySelector('[data-pdp2-zoom]');

    initStripDrag(hero.querySelector('.pdp2-hero__strip'));
    initAddToCart(hero);

    // measureFold writes a layout-affecting custom property and placeBackdrop
    // immediately reads geometry, so running this raw on every resize event
    // forces a synchronous layout per event. Coalesce into one frame.
    var placeNow = function () {
      measureFold(hero);
      placeBackdrop(hero);
    };

    var placeQueued = false;
    var placeCoalesced = function () {
      if (placeQueued) return;
      placeQueued = true;
      requestAnimationFrame(function () {
        placeQueued = false;
        placeNow();
      });
    };

    // On desktop the stylesheet keeps the backdrop hidden until it has been
    // placed once (html.pdp2-js, set inline by the hero), so it fades in where
    // it belongs instead of jumping there. Mark the hero ready after the first
    // placement with real image dimensions, or after an error, or after 3 s.
    var img = hero.querySelector('.pdp2-hero__img');
    var markReady = function () {
      if (hero.classList.contains('is-ready')) return;
      // Commit the placed position before the class turns on its transition,
      // so the first placement never animates.
      if (img) window.getComputedStyle(img).objectPosition;
      hero.classList.add('is-ready');
    };

    // Initial placement runs directly: requestAnimationFrame does not fire in a
    // backgrounded tab, and the backdrop would stay unplaced until a resize.
    placeNow();
    if (!img || img.complete) {
      markReady();
    } else {
      // Placement needs only the image's dimensions, which arrive with the
      // first bytes, so reveal well before the full download finishes.
      var waitForSize = function () {
        if (hero.classList.contains('is-ready')) return;
        if (img.naturalWidth) {
          placeNow();
          markReady();
          return;
        }
        requestAnimationFrame(waitForSize);
      };
      requestAnimationFrame(waitForSize);
      on(img, 'load', function () {
        placeNow();
        markReady();
      });
      on(img, 'error', markReady);
      setTimeout(markReady, 3000);
    }
    on(window, 'resize', placeCoalesced);

    // The buy panel grows after load (the subscription widget, prices and
    // installment badge arrive late), which changes the hero height. CSS cover
    // keeps the backdrop covered, but re-place so the jar stays over the strip.
    // Only object-position changes here, so observing the hero cannot loop.
    if (window.ResizeObserver) {
      var roQueued = false;
      var ro = new ResizeObserver(function () {
        if (roQueued) return;
        roQueued = true;
        requestAnimationFrame(function () {
          roQueued = false;
          placeBackdrop(hero);
        });
      });
      ro.observe(hero);
    }

    if (!viewer || !thumbs.length) return;

    var viewerImg = viewer.querySelector('[data-pdp2-viewer-img]');
    var count = viewer.querySelector('[data-pdp2-viewer-count]');
    var sources = thumbs.map(function (t) {
      var i = t.querySelector('img');
      return {
        src: i ? i.currentSrc || i.src : '',
        alt: t.getAttribute('aria-label') || ''
      };
    });
    var index = 0;
    var lastFocused = null;

    var swapToken = 0;

    // Swapping used to fade out, set src on a timer, then fade back in — which
    // showed the gap while the new file was still decoding. Now the next image
    // is decoded off-screen first and only then put on screen, so the crossfade
    // never reveals a blank or a stale frame.
    function render(animate) {
      var item = sources[index];
      if (!item) return;
      var token = ++swapToken;
      var src = item.src.replace(/(\?|&)width=\d+/, '$1width=1600');

      if (count) count.textContent = index + 1 + ' / ' + sources.length;
      thumbs.forEach(function (t, i) {
        var isActive = i === index;
        t.classList.toggle('is-active', isActive);
        // The class alone is invisible to assistive tech.
        if (isActive) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });

      var apply = function () {
        if (token !== swapToken) return; // a newer swap already won
        viewerImg.src = src;
        viewerImg.alt = item.alt;
        viewerImg.classList.remove('is-swapping');
      };

      // On failure, keep the image that is already showing rather than swapping
      // in a broken URL and leaving the viewer blank.
      var abandon = function () {
        if (token !== swapToken) return;
        viewerImg.classList.remove('is-swapping');
      };

      if (!animate || reduceMotion.matches) {
        apply();
        return;
      }

      var next = new Image();
      next.src = src;
      // Only fade out once the replacement is actually ready, so a slow network
      // shows the current image rather than an empty frame.
      if (next.decode) {
        next.decode().then(apply).catch(abandon);
      } else if (next.complete) {
        apply();
      } else {
        next.onload = apply;
        next.onerror = abandon;
      }
    }

    var closeTimer = null;

    function open(i) {
      // A close is animating: cancel it, otherwise its pending timer hides the
      // viewer we are about to reopen.
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      var wasOpen = !viewer.hidden && viewer.classList.contains('is-open');
      if (!wasOpen) lastFocused = document.activeElement;
      index = i;
      render(wasOpen);
      if (wasOpen) return; // already on screen, just swap the image
      viewer.hidden = false;
      hero.classList.add('is-viewing');
      // Force a reflow so the transition has a start value, then open
      // synchronously. requestAnimationFrame does not fire in a backgrounded or
      // non-painting tab, which would leave the viewer shown but at opacity 0.
      void viewer.offsetHeight;
      viewer.classList.add('is-open');
      var closeBtn = viewer.querySelector('[data-pdp2-viewer-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
      // A thumbnail tap can happen with the hero partly scrolled away. Bring
      // its image back below the sticky header without moving the viewer out
      // of the image's bounds.
      if (!window.matchMedia(DESKTOP).matches) {
        var header = document.querySelector('.header__outer-wrapper');
        var headerHeight = header ? Math.max(0, header.getBoundingClientRect().bottom) : 60;
        var heroTop = hero.getBoundingClientRect().top;
        if (heroTop < headerHeight) {
          window.scrollTo({ top: Math.max(0, window.scrollY + heroTop - headerHeight), behavior: 'auto' });
        }
      }
    }

    function close() {
      viewer.classList.remove('is-open');
      hero.classList.remove('is-viewing');
      // The highlight tracks what the viewer is showing, so it goes with it.
      thumbs.forEach(function (t) {
        t.classList.remove('is-active');
        t.removeAttribute('aria-current');
      });
      var finish = function () {
        closeTimer = null;
        viewer.hidden = true;
      };
      if (reduceMotion.matches) finish();
      else closeTimer = setTimeout(finish, 320);
      // Never restore focus into the viewer itself.
      if (lastFocused && lastFocused.focus && !viewer.contains(lastFocused)) lastFocused.focus();
    }

    function step(delta) {
      index = (index + delta + sources.length) % sources.length;
      render(true);
    }

    /* Swipe or drag across the image to change it. On mobile this is the only
     * control inside the viewer, since the arrow bar is hidden there. Works
     * with touch and with a mouse. */
    (function initViewerSwipe() {
      if (!window.PointerEvent) return;
      var stage = viewer.querySelector('.pdp2-viewer__stage') || viewer;
      var active = false;
      var startX = 0;
      var startY = 0;
      var dx = 0;
      var axis = null;

      on(stage, 'pointerdown', function (e) {
        active = true;
        startX = e.clientX;
        startY = e.clientY;
        dx = 0;
        axis = null;
      });

      on(stage, 'pointermove', function (e) {
        if (!active) return;
        dx = e.clientX - startX;
        var dy = e.clientY - startY;
        // Decide the gesture's axis once, so a vertical scroll is never stolen.
        if (axis === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
          axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        if (axis === 'x') {
          stage.classList.add('is-swiping');
          e.preventDefault();
        }
      });

      function end() {
        if (!active) return;
        active = false;
        stage.classList.remove('is-swiping');
        if (axis === 'x' && Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
        axis = null;
        dx = 0;
      }

      on(stage, 'pointerup', end);
      on(stage, 'pointercancel', end);
      on(stage, 'pointerleave', end);
    })();

    // The gallery strip stays live while the viewer is open, so a thumbnail
    // either opens the viewer or swaps the image already in it.
    thumbs.forEach(function (t, i) {
      on(t, 'click', function () {
        open(i);
      });
    });
    on(zoom, 'click', function () {
      open(index);
    });
    on(viewer.querySelector('[data-pdp2-viewer-prev]'), 'click', function () {
      step(-1);
    });
    on(viewer.querySelector('[data-pdp2-viewer-next]'), 'click', function () {
      step(1);
    });
    on(viewer.querySelector('[data-pdp2-viewer-close]'), 'click', close);

    // Click anywhere outside the viewer closes it. Thumbnails are excluded so
    // they keep swapping the image rather than closing and reopening.
    on(document, 'click', function (e) {
      if (viewer.hidden || !viewer.classList.contains('is-open')) return;
      if (viewer.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-pdp2-thumb], [data-pdp2-zoom]')) return;
      close();
    });

    on(document, 'keydown', function (e) {
      if (viewer.hidden) return;
      if (e.key === 'Escape') {
        close();
      } else if (e.key === 'ArrowLeft') {
        step(-1);
      } else if (e.key === 'ArrowRight') {
        step(1);
      } else if (e.key === 'Tab') {
        // The viewer claims aria-modal="true", so focus has to stay inside it.
        var focusable = viewer.querySelectorAll(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (!viewer.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  /* ----------------------------------------------------------------------
   * Chapter navigation
   * -------------------------------------------------------------------- */
  function initTabs(nav) {
    if (nav.dataset.pdp2Init) return;
    nav.dataset.pdp2Init = '1';

    var tabs = Array.prototype.slice.call(nav.querySelectorAll('[data-pdp2-tab]'));
    var ink = nav.querySelector('[data-pdp2-ink]');
    var progress = nav.querySelector('[data-pdp2-progress]');
    var hideOnScroll = nav.dataset.hideOnScroll === 'true';
    if (!tabs.length) return;

    var targets = tabs
      .map(function (tab) {
        return { tab: tab, el: document.getElementById(tab.dataset.pdp2Tab) };
      })
      .filter(function (t) {
        return t.el;
      });

    // The ink is absolutely positioned inside the inner container, which is
    // centred and narrower than the nav on wide screens, so it is placed from
    // the tab's offset within that container, not from the nav's left edge.
    // Offsets also ignore the inner's own horizontal scroll on phones, where
    // the ink scrolls with the tabs. Positioned under reduced motion too; only
    // the glide is dropped there, by the stylesheet.
    function moveInk(tab) {
      if (!ink) return;
      ink.style.width = tab.offsetWidth + 'px';
      ink.style.transform = 'translateX(' + tab.offsetLeft + 'px)';
    }

    function setActive(tab) {
      tabs.forEach(function (t) {
        t.classList.toggle('is-active', t === tab);
      });
      moveInk(tab);
    }

    if ('IntersectionObserver' in window) {
      var seen = {};
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            seen[entry.target.id] = entry.isIntersecting ? entry.intersectionRatio : 0;
          });
          var best = null;
          var bestRatio = 0;
          targets.forEach(function (t) {
            var r = seen[t.el.id] || 0;
            if (r > bestRatio) {
              bestRatio = r;
              best = t.tab;
            }
          });
          if (best) setActive(best);
        },
        { rootMargin: '-20% 0px -60% 0px', threshold: [0, 0.25, 0.5, 1] }
      );
      targets.forEach(function (t) {
        io.observe(t.el);
      });
    }

    var lastY = window.scrollY;
    var ticking = false;
    // scrollHeight and offsetTop both force a synchronous layout. Read them on
    // resize instead of on every scroll event, and coalesce the work into one
    // frame so a fast scroll does not run this dozens of times.
    var maxScroll = 0;
    var MOBILE = window.matchMedia('(max-width: 899px)');
    // The stock line rides the right end of the strip. When the tabs and the
    // line together overflow the row, the line goes rather than being clipped
    // mid-word; the tabs keep the full width and scroll where they must.
    var stock = nav.querySelector('.pdp2-tabs__stock');
    var inner = nav.querySelector('.pdp2-tabs__inner');
    function fitStock() {
      if (!stock || !inner) return;
      stock.classList.remove('is-clipped');
      if (inner.scrollWidth > inner.clientWidth + 1) stock.classList.add('is-clipped');
    }

    function measureScroll() {
      fitStock();
      maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      // Chapters read this for scroll-margin-top, so an anchor jump lands
      // below the bar rather than under it.
      document.documentElement.style.setProperty("--pdp2-tabs-offset", nav.offsetHeight + "px");
    }

    // Clicking a tab scrolls down, which would otherwise hide the bar mid-jump
    // and leave an empty band above the chapter. Hold it on screen until the
    // smooth scroll has settled.
    var pinUntil = 0;
    // Height of everything pinned above the chapter: the theme header group
    // plus this bar. Read from the wrapper's resolved sticky top rather than
    // the custom property, which getComputedStyle returns as an unresolved
    // calc() string.
    function pinnedHeight() {
      var wrapper = nav.parentElement;
      var top = wrapper ? parseFloat(getComputedStyle(wrapper).top) : 0;
      return (isNaN(top) ? 0 : top) + nav.offsetHeight;
    }

    tabs.forEach(function (tab) {
      on(tab, "click", function (e) {
        var target = document.getElementById(tab.dataset.pdp2Tab);
        pinUntil = Date.now() + 1500;
        setActive(tab);
        if (!target) return; // plain anchor navigation

        // An anchor jump is not a scroll-in: the chapter must be readable the
        // moment it lands, not fade up a second later.
        Array.prototype.forEach.call(target.querySelectorAll("[data-pdp2-reveal]"), function (el) {
          el.classList.add("is-in");
        });

        // Scroll here rather than through the anchor, so the chapter lands
        // exactly under this bar with the bar pinned to the header. The
        // native jump adds the theme page-level scroll padding on top of the
        // chapter scroll-margin and stops short by that amount.
        e.preventDefault();
        var y = target.getBoundingClientRect().top + window.scrollY - pinnedHeight();
        window.scrollTo({ top: Math.max(0, Math.round(y)), behavior: reduceMotion.matches ? "auto" : "smooth" });
        if (history.replaceState) history.replaceState(null, "", "#" + target.id);
      });
    });

    function update() {
      ticking = false;
      var y = window.scrollY;
      if (progress) {
        nav.style.setProperty('--pdp2-progress', maxScroll > 0 ? y / maxScroll : 0);
      }
      // The design keeps one pinned element at a time on a phone: while the
      // sticky buy bar is showing, the tab strip steps aside on scroll-down and
      // returns on scroll-up. On desktop, and until a buy bar is on screen, the
      // strip never hides. Small deltas are ignored so it does not flicker.
      if (hideOnScroll && !reduceMotion.matches) {
        var dy = y - lastY;
        if (!MOBILE.matches || Date.now() < pinUntil) {
          nav.classList.remove('is-hidden');
        } else if (Math.abs(dy) >= 6) {
          var buybarOn = document.querySelector('[data-pdp2-buybar].is-on');
          nav.classList.toggle('is-hidden', dy > 0 && !!buybarOn);
        }
      }
      lastY = y;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    measureScroll();
    on(window, 'scroll', onScroll, { passive: true });
    on(window, 'resize', measureScroll);
    update();

    on(window, 'resize', function () {
      var active = nav.querySelector('.pdp2-tab.is-active');
      if (active) moveInk(active);
    });

    if (targets.length) setActive(targets[0].tab);
  }

  /* ----------------------------------------------------------------------
   * Scroll reveals
   *
   * Content is visible by default. Only when the observer is available and
   * motion is allowed does the page opt in (html.pdp2-motion), and anything
   * already inside the viewport at that moment is marked in synchronously so
   * nothing on screen blinks.
   * -------------------------------------------------------------------- */
  var revealObserver = null;

  function initReveals(scope) {
    var items = Array.prototype.slice.call(scope.querySelectorAll('[data-pdp2-reveal]:not(.is-in)'));
    if (!items.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    document.documentElement.classList.add('pdp2-motion');

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
      );
    }

    var vh = window.innerHeight;
    items.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < vh && rect.bottom > 0) {
        el.classList.add('is-in');
      } else {
        revealObserver.observe(el);
      }
    });
  }

  /* ----------------------------------------------------------------------
   * Disclosure rows
   *
   * Native <details>, so the content is in the HTML and the row works with no
   * script at all. The script only adds the height animation: on open it lets
   * the browser reveal the body and animates its height up from zero; on
   * close it animates down first and removes [open] when done.
   * -------------------------------------------------------------------- */
  function initDisclosure(details) {
    if (details.dataset.pdp2Init) return;
    details.dataset.pdp2Init = '1';

    var summary = details.querySelector('summary');
    var body = details.querySelector('.pdp2-disclosure__body');
    details.classList.toggle('is-open', details.open);
    if (!summary || !body || !body.animate) return;

    var anim = null;

    function settle(open) {
      anim = null;
      details.open = open;
      body.style.height = '';
    }

    on(summary, 'click', function (e) {
      if (reduceMotion.matches) {
        // Native toggle; only mirror the state for the chevron.
        details.classList.toggle('is-open', !details.open);
        return;
      }
      e.preventDefault();

      var opening = !details.open || (anim && !details.classList.contains('is-open'));
      if (anim) anim.cancel();
      details.classList.toggle('is-open', opening);

      var from = details.open ? body.getBoundingClientRect().height : 0;
      if (opening) details.open = true;
      var to = opening ? body.scrollHeight : 0;

      anim = body.animate(
        { height: [from + 'px', to + 'px'] },
        { duration: opening ? 420 : 320, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }
      );
      var current = anim;
      var done = function () {
        if (anim !== current) return; // superseded by a newer toggle
        current.cancel(); // drop the held height before settling
        settle(opening);
      };
      current.onfinish = done;
      current.oncancel = function () {
        if (anim === current) anim = null;
      };
      // Animations do not advance while the tab is not painting. If that
      // happens mid-toggle the row must still end up in its final state
      // rather than pinned at an intermediate height.
      setTimeout(done, 700);
    });
  }

  /* ----------------------------------------------------------------------
   * Results: before/after comparison card and the photo-pair strip
   *
   * The divider is a CSS custom property on the card; dragging, arrow keys
   * and the intro nudge all write it. A pair button carries everything the
   * card and the quote need in data attributes, so a swap never touches the
   * network beyond the two photos, which are decoded before the wipe shows
   * them.
   * -------------------------------------------------------------------- */
  function initResults(root) {
    if (root.dataset.pdp2Init) return;
    root.dataset.pdp2Init = '1';

    var card = root.querySelector('[data-pdp2-compare]');
    if (!card) return;

    var beforeImg = card.querySelector('[data-pdp2-compare-before]');
    var afterImg = card.querySelector('[data-pdp2-compare-after]');
    var MIN = 4;
    var MAX = 96;
    var REST = parseFloat(card.style.getPropertyValue('--pdp2-ba-pos')) || 52;
    var pos = REST;
    var touched = false;

    function setPos(p) {
      pos = Math.min(MAX, Math.max(MIN, p));
      card.style.setProperty('--pdp2-ba-pos', pos + '%');
      card.setAttribute('aria-valuenow', String(Math.round(pos)));
    }

    /* Drag. Pointer capture keeps the divider following a fast drag that
     * leaves the card. */
    var dragging = false;
    function fromEvent(e) {
      var r = card.getBoundingClientRect();
      return ((e.clientX - r.left) / r.width) * 100;
    }
    on(card, 'pointerdown', function (e) {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      dragging = true;
      touched = true;
      if (card.setPointerCapture) card.setPointerCapture(e.pointerId);
      setPos(fromEvent(e));
    });
    on(card, 'pointermove', function (e) {
      if (dragging) setPos(fromEvent(e));
    });
    function endDrag() {
      dragging = false;
    }
    on(card, 'pointerup', endDrag);
    on(card, 'pointercancel', endDrag);

    on(card, 'keydown', function (e) {
      var step = e.shiftKey ? 10 : 2;
      if (e.key === 'ArrowLeft') setPos(pos - step);
      else if (e.key === 'ArrowRight') setPos(pos + step);
      else if (e.key === 'Home') setPos(MIN);
      else if (e.key === 'End') setPos(MAX);
      else return;
      touched = true;
      e.preventDefault();
    });

    /* Intro nudge: once the card is well in view, the divider swings a little
     * to show it moves. Skipped under reduced motion and once touched. */
    if (!reduceMotion.matches && 'IntersectionObserver' in window) {
      var nudged = false;
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting || nudged) return;
            nudged = true;
            io.unobserve(card);
            setTimeout(function () {
              var t0 = performance.now();
              var dur = 1800;
              var tick = function (t) {
                if (touched) return;
                var p = Math.min(1, (t - t0) / dur);
                setPos(REST + Math.sin(p * Math.PI) * 9);
                if (p < 1) requestAnimationFrame(tick);
              };
              requestAnimationFrame(tick);
            }, 500);
          });
        },
        { threshold: 0.6 }
      );
      io.observe(card);
    }

    /* Pairs. */
    var pairs = Array.prototype.slice.call(root.querySelectorAll('[data-pdp2-pair]'));
    if (!pairs.length) return;

    var beforeLabel = card.querySelector('[data-pdp2-compare-before-label]');
    var afterLabel = card.querySelector('[data-pdp2-compare-after-label]');
    var name = card.querySelector('[data-pdp2-compare-name]');
    var note = card.querySelector('[data-pdp2-compare-note]');
    var quote = root.querySelector('[data-pdp2-compare-quote]');
    var quoteName = root.querySelector('[data-pdp2-compare-quote-name]');
    var quoteMeta = root.querySelector('[data-pdp2-compare-quote-meta]');
    var quoteText = root.querySelector('[data-pdp2-compare-quote-text]');

    function setText(el, text) {
      if (!el) return;
      el.textContent = text || '';
      el.hidden = !text;
    }

    function apply(btn) {
      var d = btn.dataset;
      if (afterImg && d.after) {
        afterImg.removeAttribute('srcset');
        afterImg.removeAttribute('sizes');
        afterImg.src = d.after;
        afterImg.alt = (d.name || '') + (d.afterLabel ? ', ' + d.afterLabel : '');
      }
      if (beforeImg && d.before) {
        beforeImg.removeAttribute('srcset');
        beforeImg.removeAttribute('sizes');
        beforeImg.src = d.before;
        beforeImg.alt = (d.name || '') + (d.beforeLabel ? ', ' + d.beforeLabel : '');
      }
      setText(beforeLabel, d.beforeLabel);
      setText(afterLabel, d.afterLabel);
      setText(name, d.name);
      setText(note, d.note);
      setText(quoteName, d.name);
      setText(quoteMeta, d.quoteMeta);
      setText(quoteText, d.quote);
      if (quote) quote.hidden = !d.quote;
    }

    // Decode both photos first so the wipe never reveals a half-loaded image.
    function preload(btn) {
      var urls = [btn.dataset.before, btn.dataset.after].filter(Boolean);
      return Promise.all(
        urls.map(function (u) {
          var img = new Image();
          img.src = u;
          return img.decode ? img.decode().catch(function () {}) : Promise.resolve();
        })
      );
    }

    var wiping = false;
    var ease = function (p) {
      return 1 - Math.pow(1 - p, 3);
    };

    function select(btn) {
      pairs.forEach(function (b) {
        var active = b === btn;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    pairs.forEach(function (btn) {
      on(btn, 'click', function () {
        if (wiping || btn.classList.contains('is-active')) return;
        select(btn);
        touched = true;

        if (reduceMotion.matches) {
          preload(btn).then(function () {
            apply(btn);
          });
          return;
        }

        wiping = true;
        var from = pos;
        var ready = preload(btn);
        var t0 = performance.now();
        var out = function (t) {
          var p = Math.min(1, (t - t0) / 320);
          setPos(from + (MIN - from) * ease(p));
          if (p < 1) return requestAnimationFrame(out);
          ready.then(function () {
            apply(btn);
            var t1 = performance.now();
            var back = function (tt) {
              var q = Math.min(1, (tt - t1) / 720);
              setPos(MIN + (REST - MIN) * ease(q));
              if (q < 1) requestAnimationFrame(back);
              else wiping = false;
            };
            requestAnimationFrame(back);
          });
        };
        requestAnimationFrame(out);
        // Animations do not advance in a tab that is not painting; never leave
        // the card stuck mid-wipe.
        setTimeout(function () {
          if (!wiping) return;
          wiping = false;
          apply(btn);
          setPos(REST);
        }, 2500);
      });
    });
  }

  /* ----------------------------------------------------------------------
   * Ingredients explorer
   *
   * Chips select an ingredient; the single card is updated from the data
   * baked into the section. The card is server-rendered with the first
   * ingredient, so it is complete without JavaScript. A pointer tilts the
   * glass disc for a light 3D read.
   * -------------------------------------------------------------------- */
  function initIngredients(root) {
    if (root.dataset.pdp2Init) return;
    root.dataset.pdp2Init = '1';

    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-pdp2-ing-chip]'));
    var dataEl = root.querySelector('[data-pdp2-ing-data]');
    if (!chips.length || !dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return; // leave the server-rendered first card in place
    }

    var card = root.querySelector('[data-pdp2-ing-card]');
    var spec = root.querySelector('[data-pdp2-ing-spec]');
    var orb = root.querySelector('[data-pdp2-ing-orb]');
    var tex = root.querySelector('[data-pdp2-ing-tex]');
    var use = root.querySelector('[data-pdp2-ing-use]');
    var iconWrap = root.querySelector('.pdp2-ing__disc-ic');
    var text = root.querySelector('[data-pdp2-ing-text]');
    var nameEl = root.querySelector('[data-pdp2-ing-name]');
    var inciEl = root.querySelector('[data-pdp2-ing-inci]');
    var bodyEl = root.querySelector('[data-pdp2-ing-body]');
    var roleEl = root.querySelector('[data-pdp2-ing-role]');
    var cell2 = root.querySelector('[data-pdp2-ing-cell2]');
    var metaLabel = root.querySelector('[data-pdp2-ing-meta-label]');
    var metaValue = root.querySelector('[data-pdp2-ing-meta-value]');
    var tag = root.querySelector('[data-pdp2-ing-tag]');
    var inciRows = Array.prototype.slice.call(root.querySelectorAll('[data-pdp2-ing-inci-row]'));
    var defaultTex = card ? card.getAttribute('data-pdp2-ing-default-tex') : '';
    var posCell = root.querySelector('[data-pdp2-ing-pos]');
    var posValue = root.querySelector('[data-pdp2-ing-pos-value]');

    // The complete label as the last tab on wide screens. Below 900px the
    // chips scroll sideways, so the list stays a disclosure under the card.
    var labelChip = root.querySelector('[data-pdp2-ing-label-chip]');
    var labelList = root.querySelector('[data-pdp2-ing-label]');
    var labelSummary = labelList ? labelList.querySelector('summary') : null;
    var wide = window.matchMedia('(min-width: 900px)');
    var labelMode = false; // the label tab exists (wide screen)
    var labelOn = false; // the label tab is selected

    function setText(el, value) {
      if (!el) return;
      el.textContent = value || '';
      if (el === inciEl || el === tag) el.hidden = !value;
    }

    var current = 0;

    // A disclosure toggle still animating (opened or closed on a phone) must
    // not settle after the mode has changed under it.
    function stopDisclosure() {
      var body = labelList.querySelector('.pdp2-disclosure__body');
      if (body && body.getAnimations) body.getAnimations().forEach(function (an) { an.cancel(); });
      if (body) body.style.height = '';
    }

    function setLabel(show, focusChip) {
      if (!labelChip || !labelList) return;
      stopDisclosure();
      labelOn = show;
      labelChip.classList.toggle('is-on', show);
      labelChip.setAttribute('aria-selected', show ? 'true' : 'false');
      labelChip.tabIndex = show ? 0 : -1;
      if (show) {
        chips.forEach(function (c) {
          c.classList.remove('is-on');
          c.setAttribute('aria-selected', 'false');
          c.tabIndex = -1;
        });
        if (card) card.hidden = true;
        labelList.classList.add('is-panel', 'is-open');
        labelList.open = true;
        if (labelSummary) labelSummary.tabIndex = -1;
        if (focusChip) labelChip.focus();
      } else {
        if (card) card.hidden = false;
        labelList.classList.remove('is-panel', 'is-open');
        labelList.open = false;
        if (labelSummary) labelSummary.removeAttribute('tabindex');
      }
    }

    function setLabelMode() {
      if (!labelChip || !labelList) return;
      var wanted = wide.matches;
      if (wanted === labelMode) return;
      stopDisclosure();
      if (!wanted && labelOn) {
        // The label tab is about to disappear; keep focus on a visible tab.
        var hadFocus = root.contains(document.activeElement) && (document.activeElement === labelChip || labelList.contains(document.activeElement));
        select(current, hadFocus);
      }
      labelMode = wanted;
      labelChip.hidden = !wanted;
      root.classList.toggle('has-label-tab', wanted);
      if (wanted) {
        labelList.setAttribute('role', 'tabpanel');
        labelList.setAttribute('aria-labelledby', labelChip.id);
        labelList.tabIndex = 0;
        labelList.open = false;
        labelList.classList.remove('is-open');
      } else {
        labelList.removeAttribute('role');
        labelList.removeAttribute('aria-labelledby');
        labelList.removeAttribute('tabindex');
      }
    }

    function select(i, focusChip) {
      var d = data[i];
      if (!d) return;
      current = i;
      if (labelOn) setLabel(false);

      chips.forEach(function (c, n) {
        var on = n === i;
        c.classList.toggle('is-on', on);
        c.setAttribute('aria-selected', on ? 'true' : 'false');
        c.tabIndex = on ? 0 : -1;
        if (on && focusChip) c.focus();
      });

      // Point the shared panel at the selected tab, per the tabs pattern.
      if (card && chips[i]) card.setAttribute('aria-labelledby', chips[i].id);

      // Show this ingredient's own image, or restore the tinted default
      // texture. The photo modifier turns the tint off for a real photo.
      if (card) {
        card.style.setProperty('--pdp2-ing-hue', (d.hue || 0) + 'deg');
        card.style.setProperty('--pdp2-ing-sat', d.sat == null ? 1 : d.sat);
        card.classList.toggle('pdp2-ing__card--photo', !!d.image);
      }
      if (tex) {
        tex.removeAttribute('srcset');
        tex.removeAttribute('sizes');
        tex.src = d.image || defaultTex;
        tex.alt = ''; // decorative in every case; the facts are in the text
      }
      if (use && d.icon) use.setAttribute('href', '#pdp2-ing-' + d.icon);

      setText(nameEl, d.name);
      setText(inciEl, d.inci);
      setText(bodyEl, d.body);
      setText(roleEl, d.role);
      setText(tag, d.tag);

      if (cell2) {
        var hasMeta = d.metaLabel && d.metaValue;
        cell2.hidden = !hasMeta;
        if (hasMeta) {
          setText(metaLabel, d.metaLabel);
          setText(metaValue, d.metaValue);
        }
      }

      if (posCell && posValue) {
        posCell.hidden = !d.inciIndex;
        if (d.inciIndex) posValue.textContent = (posValue.getAttribute('data-pdp2-ing-pos-format') || '[n]').replace('[n]', d.inciIndex);
      }

      // Highlight the matching row in the open label list.
      inciRows.forEach(function (row) {
        row.classList.toggle('is-hit', d.inciIndex && Number(row.getAttribute('data-pdp2-ing-inci-row')) === d.inciIndex);
      });

      // Replay the swap animations.
      if (!reduceMotion.matches) {
        if (text) {
          text.classList.remove('is-swap');
          void text.offsetWidth;
          text.classList.add('is-swap');
        }
        if (iconWrap) {
          iconWrap.classList.remove('is-swap');
          void iconWrap.offsetWidth;
          iconWrap.classList.add('is-swap');
        }
      }
    }

    chips.forEach(function (chip, i) {
      on(chip, 'click', function () {
        select(i, false);
      });
    });

    if (labelChip && labelList) {
      on(labelChip, 'click', function () {
        setLabel(true, false);
      });
      setLabelMode();
      if (wide.addEventListener) wide.addEventListener('change', setLabelMode);
      else if (wide.addListener) wide.addListener(setLabelMode);
    }

    // Roving-tabindex arrow-key navigation across the tablist. When the
    // label tab is shown it is the last position.
    var tablist = root.querySelector('.pdp2-ing__chips');
    on(tablist, 'keydown', function (e) {
      var count = chips.length + (labelMode ? 1 : 0);
      var at = labelOn ? chips.length : current;
      var go = function (n) {
        if (labelMode && n === chips.length) setLabel(true, true);
        else select(n, true);
      };
      var delta = 0;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') delta = 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') delta = -1;
      else if (e.key === 'Home') return go(0), e.preventDefault();
      else if (e.key === 'End') return go(count - 1), e.preventDefault();
      else return;
      e.preventDefault();
      go((at + delta + count) % count);
    });

    // Disc tilt follows the pointer on devices that have one.
    if (spec && orb && !reduceMotion.matches) {
      on(spec, 'pointermove', function (e) {
        if (e.pointerType === 'touch') return;
        // Reduced motion can be turned on after load; stand down and rest flat.
        if (reduceMotion.matches) {
          orb.style.setProperty('--pdp2-ing-ry', '0deg');
          orb.style.setProperty('--pdp2-ing-rx', '0deg');
          return;
        }
        var r = spec.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width - 0.5;
        var y = (e.clientY - r.top) / r.height - 0.5;
        orb.style.setProperty('--pdp2-ing-ry', (x * 22).toFixed(2) + 'deg');
        orb.style.setProperty('--pdp2-ing-rx', (-y * 18).toFixed(2) + 'deg');
      });
      on(spec, 'pointerleave', function () {
        orb.style.setProperty('--pdp2-ing-ry', '0deg');
        orb.style.setProperty('--pdp2-ing-rx', '0deg');
      });
    }

    // Sync the highlighted row when the list is first opened.
    var list = root.querySelector('[data-pdp2-ing-inci-grid]');
    var details = list && list.closest('details');
    if (details) {
      on(details, 'toggle', function () {
        if (!details.open) return;
        var d = data[current];
        inciRows.forEach(function (row) {
          row.classList.toggle('is-hit', d && d.inciIndex && Number(row.getAttribute('data-pdp2-ing-inci-row')) === d.inciIndex);
        });
      });
    }
  }

  /* ----------------------------------------------------------------------
   * How to use: morning / evening toggle
   *
   * The segment swaps each step's body (and image, where an evening variant
   * is given), slides the pill, deepens the band and replays a card turn.
   * The step content is server-rendered with the morning routine, so it is
   * complete and readable without JavaScript.
   * -------------------------------------------------------------------- */
  function initHowto(root) {
    if (root.dataset.pdp2Init) return;
    root.dataset.pdp2Init = '1';

    var seg = root.querySelector('[data-pdp2-seg]');
    var btns = Array.prototype.slice.call(root.querySelectorAll('[data-pdp2-seg-btn]'));
    var thumb = root.querySelector('[data-pdp2-seg-thumb]');
    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-pdp2-step]'));
    if (!seg || btns.length < 2) return;

    // Capture each step's morning image in full (src + the responsive
    // candidates), so returning to morning restores responsive selection, and
    // mark it as the shown state.
    steps.forEach(function (step) {
      var img = step.querySelector('[data-pdp2-step-img]');
      if (img) {
        step._morning = {
          src: img.getAttribute('src') || '',
          srcset: img.getAttribute('srcset') || '',
          sizes: img.getAttribute('sizes') || '',
          alt: img.getAttribute('alt') || ''
        };
        img._pdp2State = 'morning';
      }
    });

    function placeThumb() {
      if (!thumb) return;
      var active = btns[current];
      thumb.style.width = active.offsetWidth + 'px';
      thumb.style.transform = 'translateX(' + (active.offsetLeft - 4) + 'px)';
    }

    var current = 0;

    // Swap an image to a target descriptor {src, srcset, sizes, alt}, tracking
    // which state is showing so a repeat is a no-op, and cancelling any swap
    // still mid-fade so a fast toggle can never settle on the wrong image.
    function swapImage(img, state, target) {
      if (!img || !target.src) return;
      if (img._pdp2Timer) {
        clearTimeout(img._pdp2Timer);
        img._pdp2Timer = null;
      }
      if (img._pdp2State === state) {
        img.classList.remove('is-fading');
        img.alt = target.alt || '';
        return;
      }
      img._pdp2State = state;

      var apply = function () {
        img.src = target.src;
        if (target.srcset) img.setAttribute('srcset', target.srcset);
        else img.removeAttribute('srcset');
        if (target.sizes) img.setAttribute('sizes', target.sizes);
        else img.removeAttribute('sizes');
        img.alt = target.alt || '';
      };

      if (reduceMotion.matches) {
        apply();
        img.classList.remove('is-fading');
        return;
      }
      img.classList.add('is-fading');
      img._pdp2Timer = setTimeout(function () {
        img._pdp2Timer = null;
        apply();
        // Two frames so the new image is set while still faded, then fade in.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            img.classList.remove('is-fading');
          });
        });
      }, 180);
    }

    var panel = root.querySelector('[role="tabpanel"]');

    function setTime(key, i, focusBtn) {
      current = i;
      root.setAttribute('data-time', key);
      btns.forEach(function (b, n) {
        var on = n === i;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
        if (on && focusBtn) b.focus();
      });
      // The single panel shows the selected routine; label it by that tab.
      if (panel && btns[i]) panel.setAttribute('aria-labelledby', btns[i].id);
      placeThumb();

      steps.forEach(function (step, n) {
        var body = step.querySelector('[data-pdp2-step-body]');
        if (body) {
          var m = step.getAttribute('data-morning-body');
          var e = step.getAttribute('data-evening-body');
          body.textContent = key === 'evening' ? e : m;
        }
        var img = step.querySelector('[data-pdp2-step-img]');
        var eve = step.querySelector('[data-pdp2-step-evening-img]');
        if (img) {
          if (key === 'evening' && eve) {
            swapImage(img, 'evening', {
              src: eve.getAttribute('data-pdp2-step-evening-img'),
              alt: eve.getAttribute('data-pdp2-step-evening-alt')
            });
          } else {
            swapImage(img, 'morning', step._morning);
          }
        }
        if (!reduceMotion.matches) {
          step.classList.remove('is-swap');
          void step.offsetWidth;
          step.style.setProperty('--pdp2-step-d', n * 90 + 'ms');
          step.classList.add('is-swap');
        }
      });
    }

    btns.forEach(function (b, i) {
      on(b, 'click', function () {
        if (i === current) return;
        setTime(b.getAttribute('data-pdp2-seg-btn'), i, false);
      });
    });

    // Roving-tabindex arrow navigation across the two tabs.
    on(seg, 'keydown', function (e) {
      var delta = 0;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') delta = 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') delta = -1;
      else return;
      e.preventDefault();
      var next = (current + delta + btns.length) % btns.length;
      setTime(btns[next].getAttribute('data-pdp2-seg-btn'), next, true);
    });

    // The pill is sized from the rendered buttons, so place it once fonts and
    // layout have settled, and again on resize.
    placeThumb();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(placeThumb);
    on(window, 'resize', placeThumb);
  }

  /* ----------------------------------------------------------------------
   * Lab & research: study to reference cross-highlight
   *
   * Hovering or focusing a study lights its matching reference, and the
   * reverse. Everything else in this chapter (the certificate draw, the stat
   * count-in, the evidence key) is driven by the reveal class in CSS.
   * -------------------------------------------------------------------- */
  function initLab(root) {
    if (root.dataset.pdp2Init) return;
    root.dataset.pdp2Init = '1';

    // Desktop has room for the reference list, so show it open; phones keep it
    // closed so it does not take over the screen (owner, 2026-10-12).
    var refsOpen = root.querySelector('[data-pdp2-lab-refs]');
    if (refsOpen && window.matchMedia('(min-width: 900px)').matches) refsOpen.open = true;

    // Index the references by their value, so a study is matched by lookup
    // rather than by building a selector string from user-entered numbers.
    var refItems = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-pdp2-lab-ref-item]'), function (ref) {
      var key = ref.getAttribute('data-pdp2-lab-ref-item');
      if (key != null) refItems[key] = ref;
    });

    // Several studies can cite the same reference, so a reference is lit while
    // any of its sources is active. Counting sources stops one study's
    // pointerleave from dimming a reference another study still holds.
    var refCount = {};
    function setRef(ref, key, active) {
      refCount[key] = (refCount[key] || 0) + (active ? 1 : -1);
      if (refCount[key] < 0) refCount[key] = 0;
      ref.classList.toggle('is-lit', refCount[key] > 0);
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-pdp2-lab-study]'), function (study) {
      var link = study.querySelector('[data-pdp2-lab-ref]');
      if (!link) return;
      var key = link.getAttribute('data-pdp2-lab-ref');
      var ref = refItems[key];
      if (!ref) return;

      // Track hover and focus separately so releasing one does not cancel the
      // other (focus a study, hover away, it stays lit while focused).
      var hovering = false;
      var focused = false;
      function sync(nextHover, nextFocus) {
        var was = hovering || focused;
        if (nextHover != null) hovering = nextHover;
        if (nextFocus != null) focused = nextFocus;
        var now = hovering || focused;
        if (now === was) return;
        setRef(ref, key, now);
      }

      on(study, 'pointerenter', function () {
        sync(true, null);
      });
      on(study, 'pointerleave', function () {
        sync(false, null);
      });
      on(link, 'focus', function () {
        sync(null, true);
      });
      on(link, 'blur', function () {
        sync(null, false);
      });
      // Hovering the reference lights just its own studies' marker.
      on(ref, 'pointerenter', function () {
        study.classList.add('is-lit');
      });
      on(ref, 'pointerleave', function () {
        study.classList.remove('is-lit');
      });
    });

    /* Footnote and marker clicks scroll to the reference and flash it, offset
     * so it clears the pinned header and tab bar. */
    function pinnedOffset() {
      var w = document.querySelector('.shopify-section--pdp2-tabs');
      if (!w) return 100;
      var top = parseFloat(getComputedStyle(w).top);
      return (isNaN(top) ? 0 : top) + w.offsetHeight + 16;
    }

    var flashTimer = null;
    Array.prototype.forEach.call(root.querySelectorAll('a[data-pdp2-lab-ref]'), function (link) {
      on(link, 'click', function (e) {
        var key = link.getAttribute('data-pdp2-lab-ref');
        var ref = refItems[key];
        if (!ref) return; // let the browser follow the href as a fallback
        e.preventDefault();
        // The list sits in a closed dropdown: open it before measuring.
        var refsBox = root.querySelector('[data-pdp2-lab-refs]');
        if (refsBox && !refsBox.open) refsBox.open = true;
        var y = ref.getBoundingClientRect().top + window.scrollY - pinnedOffset();
        window.scrollTo({ top: Math.max(0, Math.round(y)), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        if (history.replaceState) history.replaceState(null, '', '#' + ref.id);
        // Move keyboard focus to the reference so Tab continues from here, but
        // keep our offset scroll (preventScroll stops the browser re-jumping).
        ref.setAttribute('tabindex', '-1');
        ref.focus({ preventScroll: true });
        if (flashTimer) clearTimeout(flashTimer);
        Array.prototype.forEach.call(root.querySelectorAll('.is-flash'), function (el) {
          el.classList.remove('is-flash');
        });
        ref.classList.add('is-flash');
        flashTimer = setTimeout(function () {
          ref.classList.remove('is-flash');
        }, 1800);
      });
    });

    /* In-page report modal, so the visitor never leaves the page. */
    initLabModal(root);
  }

  function initLabModal(root) {
    var trigger = root.querySelector('[data-pdp2-lab-report-open]');
    var modal = root.querySelector('[data-pdp2-lab-modal]');
    if (!trigger || !modal) return;

    // The theme's .section carries z-index: 0, which traps a fixed child below
    // the sticky header. Portal the modal to the body so it covers everything.
    if (modal.parentNode !== document.body) document.body.appendChild(modal);

    var panel = modal.querySelector('.pdp2-lab__modal-panel');
    var frame = modal.querySelector('[data-pdp2-lab-modal-frame]');
    var url = trigger.getAttribute('data-pdp2-lab-report-url');
    var closeTimer = null;
    var lastFocused = null;
    var isOpen = false;

    function open(e) {
      if (e) e.preventDefault();
      if (isOpen) return;
      isOpen = true;
      lastFocused = document.activeElement;
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      // Load the report only when the modal is opened.
      if (frame && url && frame.getAttribute('src') !== url) frame.setAttribute('src', url);
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      // Force a reflow so the open transition has a start value.
      void modal.offsetHeight;
      modal.classList.add('is-open');
      var closeBtn = modal.querySelector('.pdp2-lab__modal-close');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      if (!isOpen) return; // idempotent: repeated Escape or clicks are no-ops
      isOpen = false;
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
      // Cancel any close still pending so it cannot fire against a reopen.
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      var finish = function () {
        closeTimer = null;
        if (isOpen) return; // reopened during the animation; leave it be
        modal.hidden = true;
        // Drop the src so a background PDF stops using resources.
        if (frame) frame.removeAttribute('src');
      };
      if (reduceMotion.matches) finish();
      else closeTimer = setTimeout(finish, 400);
      if (lastFocused && lastFocused.focus && !modal.contains(lastFocused)) lastFocused.focus();
    }

    on(trigger, 'click', open);

    // The whole certificate card opens the report too. The link inside it stays
    // the keyboard and no-JS path; this only widens the pointer target. Clicks
    // on the link itself (or any other control) are left to their own handler,
    // and a click that ends a text selection is not treated as a tap.
    var card = root.querySelector('[data-pdp2-lab-report]');
    if (card && card.contains(trigger)) {
      card.classList.add('is-clickable');
      on(card, 'click', function (e) {
        if (e.target.closest('a, button')) return;
        var sel = window.getSelection && window.getSelection();
        if (sel && String(sel).length) return;
        open(e);
      });
    }

    Array.prototype.forEach.call(modal.querySelectorAll('[data-pdp2-lab-modal-close]'), function (el) {
      on(el, 'click', close);
    });

    // A named handler so it can be removed if the theme editor re-renders.
    function onKeydown(e) {
      if (modal.hidden) return;
      if (e.key === 'Escape') {
        close();
      } else if (e.key === 'Tab') {
        // Keep focus inside the dialog chrome. The cross-origin PDF iframe is
        // deliberately excluded, so focus never enters a frame we cannot hear
        // keys from; mouse users still scroll it, keyboard users open it in a
        // tab. Escape therefore always works.
        var focusable = Array.prototype.filter.call(
          panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
          function (el) {
            // a[href] catches links even when they carry tabindex="-1" (the
            // hidden fallback link), so reject those explicitly, and skip
            // anything not rendered.
            return (el.offsetWidth > 0 || el.offsetHeight > 0) && el.getAttribute('tabindex') !== '-1';
          }
        );
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (!panel.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKeydown);

    // Theme-editor teardown: a re-render replaces the section but leaves the
    // portaled modal and this listener orphaned, so remove them on unload.
    var sectionId = root.getAttribute('data-section-id');
    if (sectionId) {
      pdp2LabTeardown[sectionId] = function () {
        document.removeEventListener('keydown', onKeydown);
        if (closeTimer) clearTimeout(closeTimer);
        if (isOpen) document.body.style.overflow = '';
        if (modal.parentNode) modal.parentNode.removeChild(modal);
        delete pdp2LabTeardown[sectionId];
      };
    }
  }

  // Portaled lab modals to clean up when their section is unloaded in the editor.
  var pdp2LabTeardown = {};

  function initFaq(root) {
    if (root.dataset.pdp2Init) return;
    root.dataset.pdp2Init = '1';

    var items = Array.prototype.slice.call(root.querySelectorAll('[data-pdp2-faq-item]'));
    items.forEach(function (item) {
      var btn = item.querySelector('[data-pdp2-faq-btn]');
      if (!btn) return;
      on(btn, 'click', function () {
        var willOpen = !item.classList.contains('is-open');
        // Single-open: collapse every card, then open the clicked one.
        items.forEach(function (other) {
          other.classList.remove('is-open');
          var b = other.querySelector('[data-pdp2-faq-btn]');
          if (b) b.setAttribute('aria-expanded', 'false');
        });
        if (willOpen) {
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });

    // Ambient parallax on the field and the two columns. Decoration only, so it
    // is skipped entirely under reduced motion.
    if (reduceMotion.matches) return;
    var field = root.querySelector('[data-pdp2-faq-bg]');
    var cols = Array.prototype.slice.call(root.querySelectorAll('[data-pdp2-faq-col]'));
    if (!field && !cols.length) return;
    var ticking = false;
    function frame() {
      ticking = false;
      if (!root.isConnected) return; // orphaned after an editor re-render
      var rect = root.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.bottom < -vh || rect.top > vh * 2) return; // far off-screen
      // -1 (section below the fold) to 1 (above), 0 at centre.
      var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
      if (progress < -1.5) progress = -1.5;
      if (progress > 1.5) progress = 1.5;
      if (field) field.style.setProperty('--pdp2-faq-bgy', (progress * -26).toFixed(1) + 'px');
      cols.forEach(function (col) {
        var depth = parseFloat(col.getAttribute('data-depth')) || 0;
        col.style.setProperty('--pdp2-faq-pz', (progress * depth * -10).toFixed(1) + 'px');
      });
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(frame);
    }
    on(window, 'scroll', onScroll, { passive: true });
    on(window, 'resize', onScroll);
    frame();
  }

  function initClosing(root) {
    if (root.dataset.pdp2Init) return;
    root.dataset.pdp2Init = '1';
    initAddToCart(root, 'form[data-pdp2-closing-form]');
    if (reduceMotion.matches) return;
    var bg = root.querySelector('[data-pdp2-closing-bg]');
    if (!bg) return;
    var ticking = false;
    function frame() {
      ticking = false;
      if (!root.isConnected) return;
      var rect = root.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.bottom < -vh || rect.top > vh * 2) return;
      var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
      if (progress < -1.5) progress = -1.5;
      if (progress > 1.5) progress = 1.5;
      // The image is oversized (height 124%), so it can slide without gaps.
      bg.style.setProperty('--pdp2-closing-par', (progress * 34).toFixed(1) + 'px');
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(frame);
    }
    on(window, 'scroll', onScroll, { passive: true });
    on(window, 'resize', onScroll);
    frame();
  }

  /* ---- Mechanism: the step crossing the middle of the screen drives the
     stage (glyph, caption, counter, ring) and the rail. Everything is
     readable before this runs; .is-ready switches on the dim states. ---- */
  var pdp2PathTeardown = {};
  function initPath(root) {
    if (root.dataset.pdp2Init) return;
    root.dataset.pdp2Init = '1';

    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-pdp2-path-step]'));
    if (steps.length < 2 || !('IntersectionObserver' in window)) return;
    var glyphs = root.querySelectorAll('[data-pdp2-path-glyph]');
    var captions = root.querySelectorAll('[data-pdp2-path-caption]');
    var photos = root.querySelectorAll('[data-pdp2-path-photo]');
    var count = root.querySelector('[data-pdp2-path-count]');
    var ring = root.querySelector('[data-pdp2-path-ring]');
    var rail = root.querySelector('[data-pdp2-path-rail]');
    var n = steps.length;
    var current = -1;

    function pad(i) {
      return (i < 9 ? '0' : '') + (i + 1);
    }

    function setStep(i) {
      if (i === current) return;
      current = i;
      steps.forEach(function (s, k) {
        s.classList.toggle('is-on', k === i);
      });
      Array.prototype.forEach.call(glyphs, function (g, k) {
        g.classList.toggle('is-on', k === i);
      });
      Array.prototype.forEach.call(captions, function (c, k) {
        c.classList.toggle('is-on', k === i);
      });
      Array.prototype.forEach.call(photos, function (ph, k) {
        ph.classList.toggle('is-on', k === i);
      });
      if (count) count.textContent = pad(i);
      if (ring) ring.style.setProperty('--pdp2-path-ring', (((i + 1) / n) * 100).toFixed(2));
      if (rail) rail.style.setProperty('--pdp2-path-fill', n > 1 ? (i / (n - 1)).toFixed(3) : 1);
    }

    // The active step is the last one whose top has passed 55% of the screen;
    // once the end of the list is in view the last step wins, so it lights up
    // even though it can never reach the middle before the chapter ends.
    var list = root.querySelector('[data-pdp2-path-steps]');
    var ticking = false;
    function frame() {
      ticking = false;
      var vh = window.innerHeight;
      var i = 0;
      steps.forEach(function (s, k) {
        if (s.getBoundingClientRect().top < vh * 0.55) i = k;
      });
      if (list && list.getBoundingClientRect().bottom < vh * 0.85) i = n - 1;
      setStep(i);
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      raf = requestAnimationFrame(frame);
    }
    var near = false;
    var raf = 0;
    var io = new IntersectionObserver(function (entries) {
      near = entries[0].isIntersecting;
      if (near) onScroll();
    }, { rootMargin: '20% 0px 20% 0px' });
    io.observe(root);
    function onWinScroll() {
      if (near) onScroll();
    }
    window.addEventListener('scroll', onWinScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    // Theme editor: undo everything when this section is unloaded or re-rendered.
    var sid = root.dataset.sectionId;
    if (sid) {
      if (pdp2PathTeardown[sid]) pdp2PathTeardown[sid]();
      pdp2PathTeardown[sid] = function () {
        io.disconnect();
        window.removeEventListener('scroll', onWinScroll);
        window.removeEventListener('resize', onScroll);
        if (raf) cancelAnimationFrame(raf);
        delete pdp2PathTeardown[sid];
      };
    }

    setStep(0);
    root.classList.add('is-ready');
    frame();
  }

  /* ----------------------------------------------------------------------
   * Video: poster cover plays the native player; topic chips seek and light
   * up while their part plays. Without script the native player stands alone.
   * -------------------------------------------------------------------- */
  function initVideo(root) {
    if (root.dataset.pdp2Init) return;
    root.dataset.pdp2Init = '1';

    var media = root.querySelector('[data-pdp2-video-media]');
    var cover = root.querySelector('[data-pdp2-video-play]');
    if (!media) return;
    root.classList.add('is-enhanced');

    function play(at) {
      root.classList.add('is-playing');
      if (typeof at === 'number' && !isNaN(at)) {
        try { media.currentTime = at; } catch (e) {}
      }
      var p = media.play();
      if (p && p.catch) p.catch(function () {});
      // The cover button disappears; hand keyboard focus to the native player.
      try { media.focus({ preventScroll: true }); } catch (e) { media.focus(); }
    }

    if (cover) on(cover, 'click', function () { play(); });

    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-pdp2-video-seek]'));
    chips.forEach(function (chip) {
      on(chip, 'click', function () { play(parseFloat(chip.getAttribute('data-pdp2-video-seek'))); });
    });

    if (chips.length) {
      var starts = chips.map(function (c) { return parseFloat(c.getAttribute('data-pdp2-video-seek')) || 0; });
      on(media, 'timeupdate', function () {
        var t = media.currentTime, active = -1;
        starts.forEach(function (s, i) { if (t >= s) active = i; });
        chips.forEach(function (c, i) { c.classList.toggle('is-on', i === active); });
      });
    }
  }

  /* ---- Value: the one-time / subscribe toggle swaps every price, the lines
     under them, the button label and the plan input. Without script the
     cards show the one-time state. ---- */
  var pdp2ValueTeardown = {};
  function initValue(root) {
    if (root.dataset.pdp2Init) return;
    root.dataset.pdp2Init = '1';
    root.classList.add('is-ready');
    initAddToCart(root, 'form[data-pdp2-value-form]');

    var btns = Array.prototype.slice.call(root.querySelectorAll('[data-pdp2-value-mode]'));
    var thumb = root.querySelector('[data-pdp2-seg-thumb]');
    var swaps = Array.prototype.slice.call(root.querySelectorAll('[data-pdp2-value-swap]'));
    var plan = root.querySelector('[data-pdp2-value-plan]');
    var current = 0;
    var timer = null;

    function placeThumb() {
      if (!thumb || !btns[current] || !btns[current].offsetWidth) return;
      thumb.style.width = btns[current].offsetWidth + 'px';
      thumb.style.transform = 'translateX(' + (btns[current].offsetLeft - 4) + 'px)';
    }

    function setMode(i) {
      if (i === current) return;
      current = i;
      var mode = btns[i].getAttribute('data-pdp2-value-mode');
      btns.forEach(function (b, n) {
        b.classList.toggle('is-on', n === i);
        b.setAttribute('aria-pressed', n === i ? 'true' : 'false');
      });
      placeThumb();
      root.classList.toggle('is-sub', mode === 'sub');
      if (plan) plan.disabled = mode !== 'sub';
      var apply = function () {
        swaps.forEach(function (el) {
          if (el.dataset[mode] != null) el.textContent = el.dataset[mode];
          el.classList.remove('is-swapping');
        });
      };
      if (timer) clearTimeout(timer);
      if (reduceMotion.matches) {
        apply();
        return;
      }
      swaps.forEach(function (el) { el.classList.add('is-swapping'); });
      timer = setTimeout(apply, 180);
    }

    btns.forEach(function (b, i) {
      on(b, 'click', function () { setMode(i); });
    });
    placeThumb();
    on(window, 'resize', placeThumb);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(placeThumb);

    // Phones: dots follow the swipe carousel and jump to a card. Cards are
    // matched to dots in visual order (this product leads via CSS order).
    var track = root.querySelector('[data-pdp2-value-track]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-pdp2-value-dot]'));
    var cardsInOrder = function () {
      return Array.prototype.slice.call(track.children).sort(function (a, b) {
        return a.getBoundingClientRect().left - b.getBoundingClientRect().left;
      });
    };
    var syncDots = function () {
      if (!track || !dots.length) return;
      var cards = cardsInOrder();
      var left = track.getBoundingClientRect().left;
      var best = 0;
      var bestDist = Infinity;
      cards.forEach(function (card, i) {
        var d = Math.abs(card.getBoundingClientRect().left - left - parseFloat(getComputedStyle(track).paddingLeft || 0));
        if (d < bestDist) { bestDist = d; best = i; }
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-on', i === best);
        dot.setAttribute('aria-current', i === best ? 'true' : 'false');
      });
    };
    // The track bleeds to the screen edges with padding equal to the page
    // gutter; snap needs the same inset in px (a % in scroll-padding would
    // refer to the track itself), so copy it across and start at the first card.
    var fitTrack = function () {
      if (!track) return;
      var pad = getComputedStyle(track).paddingLeft;
      track.style.scrollPaddingInline = pad;
      track.style.scrollPaddingLeft = pad;
    };
    if (track) {
      fitTrack();
      track.scrollLeft = 0;
      on(window, 'resize', fitTrack);
    }
    if (track && dots.length) {
      var dotTick = false;
      on(track, 'scroll', function () {
        if (dotTick) return;
        dotTick = true;
        requestAnimationFrame(function () { dotTick = false; syncDots(); });
      }, { passive: true });
      dots.forEach(function (dot, i) {
        on(dot, 'click', function () {
          var card = cardsInOrder()[i];
          if (!card) return;
          var pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
          track.scrollTo({
            left: track.scrollLeft + card.getBoundingClientRect().left - track.getBoundingClientRect().left - pad,
            behavior: reduceMotion.matches ? 'auto' : 'smooth'
          });
        });
      });
      syncDots();
    }

    var sid = root.getAttribute('data-section-id');
    if (sid) pdp2ValueTeardown[sid] = function () {
      window.removeEventListener('resize', placeThumb);
      window.removeEventListener('resize', fitTrack);
      if (timer) clearTimeout(timer);
      delete pdp2ValueTeardown[sid];
    };
  }

  /* ---- Overview: sticky columns. Each column's top is the space under the
     header and tabs, or less when the column is taller than the screen, so
     it pins by its bottom edge and its end stays reachable. ---- */
  function initOverviewColumns(root) {
    if (root.dataset.pdp2ColInit) return;
    root.dataset.pdp2ColInit = '1';
    var cols = Array.prototype.slice.call(root.querySelectorAll(':scope > .pdp2-overview__editorial, :scope > .pdp2-overview__aside'));
    if (cols.length < 2) return;
    var ticking = false;
    function measure() {
      ticking = false;
      var styles = getComputedStyle(document.documentElement);
      var base = (parseFloat(styles.getPropertyValue('--pdp2-sticky-top')) || 0) +
        (parseFloat(styles.getPropertyValue('--pdp2-tabs-offset')) || 64) + 24;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      cols.forEach(function (col) {
        var top = Math.min(base, vh - col.offsetHeight - 24);
        col.style.setProperty('--pdp2-col-top', top + 'px');
      });
    }
    function schedule() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(measure);
    }
    on(window, 'resize', schedule);
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(schedule);
      cols.forEach(function (col) { ro.observe(col); });
    }
    measure();
  }


  // Mobile buy bar: shows once the hero's buy button is above the screen, hides near the
  // closing chapter and the footer. Its button presses the hero's own Add to Bag button.
  // The buy button is looked up on every check because the subscription widget re-renders it.
  function initBuyBar(hero) {
    var bar = hero.querySelector('[data-pdp2-buybar]');
    if (!bar || bar.dataset.ready) return;
    bar.dataset.ready = '1';
    document.body.appendChild(bar); // fixed positioning must not sit inside transformed ancestors
    bar.hidden = false;
    var mobile = window.matchMedia('(max-width: 899px)');
    var addBtn = bar.querySelector('[data-pdp2-buybar-add]');
    var ticking = false;

    function heroButton() {
      return hero.querySelector('.product-form__cart-submit') || hero.querySelector('form[action*="/cart/add"]');
    }

    function render() {
      ticking = false;
      var show = false;
      if (mobile.matches) {
        var anchor = heroButton();
        var above = anchor ? anchor.getBoundingClientRect().bottom < 0 : false;
        var vh = window.innerHeight;
        var blocked = [].some.call(document.querySelectorAll('[data-pdp2-closing], .footer'), function (el) {
          var r = el.getBoundingClientRect();
          return r.top < vh && r.bottom > 0;
        });
        show = above && !blocked;
      }
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      addBtn.tabIndex = show ? 0 : -1;
      document.body.classList.toggle('pdp2-has-buybar', show);
    }

    function queue() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(render); }
    }

    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    if (mobile.addEventListener) mobile.addEventListener('change', queue);

    addBtn.addEventListener('click', function () {
      var btn = hero.querySelector('.product-form__cart-submit');
      if (btn && !btn.disabled) {
        btn.click();
      } else {
        var a = heroButton();
        if (a) a.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    render();
  }

  // Hero benefit chips stay on one line: shrink the row font until it fits (never wrap).
  function fitBenefits(row) {
    if (!row || row.dataset.pdp2Fit) return;
    row.dataset.pdp2Fit = "1";
    var fit = function () {
      row.style.removeProperty("--pdp2-benefit-fs");
      if (getComputedStyle(row).flexDirection === "column") return;
      var size = parseFloat(getComputedStyle(row.firstElementChild || row).fontSize) || 14;
      while (row.scrollWidth > row.clientWidth + 1 && size > 10) {
        size -= 0.5;
        row.style.setProperty("--pdp2-benefit-fs", size + "px");
      }
    };
    fit();
    if (window.ResizeObserver) new ResizeObserver(fit).observe(row);
    else window.addEventListener("resize", fit);
  }

  function init(root) {
    var scope = root || document;
    Array.prototype.forEach.call(scope.querySelectorAll('.pdp2-overview'), initOverviewColumns);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pdp2-value]'), initValue);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pdp2-hero]'), initHero);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pdp2-hero]'), initBuyBar);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pdp2-tabs]'), initTabs);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pdp2-disclosure]'), initDisclosure);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pdp2-results]'), initResults);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pdp2-ingredients]'), initIngredients);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pdp2-howto]'), initHowto);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pdp2-path]'), initPath);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pdp2-lab]'), initLab);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pdp2-faq]'), initFaq);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pdp2-closing]'), initClosing);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pdp2-video]'), initVideo);
    Array.prototype.forEach.call(scope.querySelectorAll(".pdp2-benefits"), fitBenefits);
    initReveals(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  // Tear down a lab section's portaled modal + listener when it is unloaded.
  document.addEventListener('shopify:section:unload', function (e) {
    var id = e.detail && e.detail.sectionId;
    if (id && pdp2LabTeardown[id]) pdp2LabTeardown[id]();
    if (id && pdp2PathTeardown[id]) pdp2PathTeardown[id]();
    if (id && pdp2ValueTeardown[id]) pdp2ValueTeardown[id]();
  });
})();
